import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const features = [
    {
      icon: 'fas fa-users',
      title: 'Student Management',
      description: 'Complete student information system with attendance, grades, and progress tracking'
    },
    {
      icon: 'fas fa-calendar-alt',
      title: 'Timetable Management',
      description: 'Automated timetable generation and management for classes and teachers'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Performance Analytics',
      description: 'Detailed reports and analytics for student performance and school operations'
    },
    {
      icon: 'fas fa-comments',
      title: 'Communication Hub',
      description: 'Seamless communication between teachers, students, and parents'
    }
  ];

  const technologies = [
    { icon: 'fas fa-mobile-alt', title: 'Mobile First', desc: 'Native Android apps for parents and faculty' },
    { icon: 'fas fa-cloud', title: 'Cloud Based', desc: 'Secure cloud infrastructure for data safety' },
    { icon: 'fas fa-shield-alt', title: 'Secure', desc: 'Advanced security measures for data protection' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            About Vidhyalaya
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Revolutionizing education through technology
          </motion.p>
        </div>
      </section>

      {/* About Content */}
      <section className="about-content">
        <div className="container">
          <div className="about-grid">
            <motion.div 
              className="about-text"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="section-badge">
                <span>Our Story</span>
              </div>
              <h2>Revolutionizing Education Management</h2>
              <p>
                Vidhyalaya is a next-generation school management platform that transforms how 
                educational institutions operate. We bridge the gap between traditional education 
                and modern technology.
              </p>
              <p>
                Our comprehensive solution empowers schools to streamline operations, enhance 
                communication, and create better learning experiences for students, teachers, 
                and parents alike.
              </p>
              <div className="about-metrics">
                <div className="metric">
                  <span className="metric-number">5+</span>
                  <span className="metric-label">Years Experience</span>
                </div>
                <div className="metric">
                  <span className="metric-number">500+</span>
                  <span className="metric-label">Schools Trust Us</span>
                </div>
                <div className="metric">
                  <span className="metric-number">24/7</span>
                  <span className="metric-label">Support Available</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="about-visual"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="visual-container">
                <motion.div 
                  className="visual-card card-primary"
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <i className="fas fa-graduation-cap"></i>
                  <h4>Smart Learning</h4>
                </motion.div>
                <motion.div 
                  className="visual-card card-secondary"
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 5, repeat: Infinity }}
                >
                  <i className="fas fa-chart-bar"></i>
                  <h4>Analytics</h4>
                </motion.div>
                <motion.div 
                  className="visual-card card-accent"
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ duration: 6, repeat: Infinity }}
                >
                  <i className="fas fa-users"></i>
                  <h4>Community</h4>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Detail */}
      <section className="features-detail">
        <div className="container">
          <motion.h2 
            className="section-title"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            What We Offer
          </motion.h2>
          <div className="features-list">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-item"
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
              >
                <div className="feature-icon">
                  <i className={feature.icon}></i>
                </div>
                <div className="feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="team">
        <div className="container">
          <motion.h2 
            className="section-title"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Our Technology
          </motion.h2>
          <div className="tech-grid">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                className="tech-item"
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                whileHover={{ y: -10 }}
              >
                <i className={tech.icon}></i>
                <h3>{tech.title}</h3>
                <p>{tech.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;